import React, { FC, useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';

import CreateGlobalCampaign from '../../components/Campaigns/CreateGlobalCampaign/CreateGlobalCampaign';
import { ICampaign } from 'reduxState/store/campaigns/types';
import EditGlobalCampaign from 'components/Campaigns/EditGlobalCampaign/EditGlobalCampaign';
import { Clear } from '@mui/icons-material';
import CampaignDashboard from 'components/Campaigns/CampaignsDashboard/CampaignDashboard';
import { useGetCampaignsQuery } from 'reduxState/store/campaigns/api';

/** Hash pattern for viewing a specific campaign: #campaign/<id> */
const CAMPAIGN_HASH_PREFIX = 'campaign';

function getCampaignIdFromHash(): number | null {
  const hash = window.location.hash.slice(1); // drop leading #
  const match = hash.startsWith(CAMPAIGN_HASH_PREFIX + '/')
    ? hash.slice(CAMPAIGN_HASH_PREFIX.length + 1)
    : null;
  if (match == null) return null;
  const id = parseInt(match, 10);
  return Number.isNaN(id) ? null : id;
}

function setCampaignHash(campaignId: number | null): void {
  const path = window.location.pathname + window.location.search;
  const url = campaignId == null ? path : `${path}#${CAMPAIGN_HASH_PREFIX}/${campaignId}`;
  window.history.replaceState(null, '', url);
}

/**
 * GlobalCampaignsManager component displays a dashboard of all campaigns with global campaign creation functionality.
 *
 * This component serves as the main page for managing campaigns globally across all applications.
 * It provides a dashboard view of existing campaigns and includes functionality for creating new
 * global campaigns. Unlike app-specific campaign managers, this component works with campaigns
 * across all applications and includes campaign duplication capabilities.
 *
 * @component
 * @returns {JSX.Element} A page displaying the global campaigns dashboard with creation form
 *
 * @example
 * <GlobalCampaignsManager />
 */

const GLOBAL_CAMPAIGNS_QUERY_PARAMS = {
  applicationIDs: [] as string[],
  merchantID: null as number | null,
  minStartDate: '',
  maxEndDate: '',
  sortBy: 'modified_date',
  sortOrder: 'asc' as const,
};

/** Wide date range so we can resolve #campaign/<id> when user opens a shared link (campaign may be outside list range). */
const HASH_RESOLVE_QUERY_PARAMS = {
  ...GLOBAL_CAMPAIGNS_QUERY_PARAMS,
  minStartDate: format(new Date(0), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  maxEndDate: format(new Date(2099, 11, 31), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
};

const GlobalCampaignsManager: FC = () => {
  const [selectedCampaignToDuplicate, setSelectedCampaignToDuplicate] = useState<ICampaign | null>(null);
  const [selectedCampaignToEdit, setSelectedCampaignToEdit] = useState<ICampaign | null>(null);
  const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false);
  const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);

  const { data: campaignsData } = useGetCampaignsQuery({
    ...GLOBAL_CAMPAIGNS_QUERY_PARAMS,
    minStartDate: format(new Date(0), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    maxEndDate: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  });

  const hashId = getCampaignIdFromHash();
  const { data: campaignsDataForHash } = useGetCampaignsQuery(HASH_RESOLVE_QUERY_PARAMS, {
    skip: hashId == null,
  });

  const handleSetSelectedCampaignToDuplicate = (campaign: ICampaign | null) => {
    setSelectedCampaignToDuplicate(campaign);
    setIsCreateCampaignModalOpen(true);
  };

  const handleSetSelectedCampaignToEdit = useCallback((campaign: ICampaign | null) => {
    setSelectedCampaignToEdit(campaign);
    setIsEditCampaignModalOpen(true);
    if (campaign != null) setCampaignHash(campaign.id);
  }, []);

  const handleOpenCreateCampaignModal = () => {
    setIsCreateCampaignModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setIsCreateCampaignModalOpen(false);
    setIsEditCampaignModalOpen(false);
    setSelectedCampaignToEdit(null);
    setSelectedCampaignToDuplicate(null);
    setCampaignHash(null);
  }, []);

  // Sync hash → state: open edit modal when URL has #campaign/<id> (e.g. shared link in new tab).
  // Use wide-date query when hash is set so we find the campaign even if it's outside the list's date range.
  useEffect(() => {
    const syncFromHash = () => {
      const id = getCampaignIdFromHash();
      if (id == null) {
        setSelectedCampaignToEdit(null);
        setIsEditCampaignModalOpen(false);
        return;
      }
      const list = campaignsData ?? [];
      const wideList = campaignsDataForHash ?? [];
      const campaign =
        list.find(c => c.id === id) ?? wideList.find(c => c.id === id) ?? null;
      if (campaign != null) {
        setSelectedCampaignToEdit(campaign);
        setIsEditCampaignModalOpen(true);
      } else if (id != null && campaignsDataForHash !== undefined) {
        // Wide list has loaded; campaign not found (e.g. deleted or invalid id) – clear hash
        if (selectedCampaignToEdit?.id !== id) {
          setCampaignHash(null);
          setSelectedCampaignToEdit(null);
          setIsEditCampaignModalOpen(false);
        }
      }
      // When hash is set we run the wide query; don't clear hash until it has loaded (above branch).
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [campaignsData, campaignsDataForHash, hashId, selectedCampaignToEdit?.id]);

  return (
    <div className="font-montserrat w-full min-w-0 overflow-x-hidden">
      <div className="flex flex-col items-center flex-1 w-full max-w-[1450px] min-w-0 mx-auto px-3 sm:px-4 box-border">
        <div className="flex flex-row flex-wrap items-center justify-between gap-3 w-full mb-3 sm:mb-4">
          <h1 className="text-muted-dark-purple m-0">Global Campaigns Manager</h1>
          <button
            type="button"
            className="shrink-0 h-[40px] px-4 font-bold text-white transition-colors rounded-md text-md bg-muted-dark-purple hover:bg-dark-purple disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-campaign-button-top"
            onClick={handleOpenCreateCampaignModal}
          >
            Add Campaign
          </button>
        </div>
        <CampaignDashboard
          handleSetSelectedDuplicatingCampaign={handleSetSelectedCampaignToDuplicate}
          handleSetSelectedEditCampaign={handleSetSelectedCampaignToEdit}
        />
        <div className="flex justify-end w-full mt-3">
          <button
            type="button"
            className="w-full sm:w-[200px] h-[40px] font-bold text-white transition-colors rounded-md text-md bg-muted-dark-purple hover:bg-dark-purple disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-campaign-button"
            onClick={handleOpenCreateCampaignModal}
          >
            Add Campaign
          </button>
        </div>

        <div
          className="absolute top-2 right-2 sm:top-[15px] sm:right-[15px] cursor-pointer z-10 p-1 touch-manipulation"
          onClick={handleCloseModal}
          data-testid="close-button"
        >
          <Clear />
        </div>

        {selectedCampaignToEdit && (
          <EditGlobalCampaign
            campaignToEdit={selectedCampaignToEdit}
            setSelectedCampaignToEdit={handleSetSelectedCampaignToEdit}
            handleCloseModal={handleCloseModal}
            isModalOpen={isEditCampaignModalOpen}
          />
        )}
        <CreateGlobalCampaign
          campaignToDuplicate={selectedCampaignToDuplicate}
          setCampaignToDuplicate={handleSetSelectedCampaignToDuplicate}
          handleCloseModal={handleCloseModal}
          isModalOpen={isCreateCampaignModalOpen}
        />
      </div>
    </div>
  );
};

export default GlobalCampaignsManager;
